<script setup lang="ts">
import { computed, defineAsyncComponent } from "vue";
export interface UiIconProps {
  name: keyof typeof ICON_PATHS;
  class?: string | string[] | Record<string, boolean>;
}

const props = withDefaults(defineProps<UiIconProps>(), {
  name: "search",
  class: "w-6 h-6",
});

const ICON_PATHS = {
  search: "../assets/vector/icons/search.svg",
  "search-md": "../assets/vector/icons/search-md.svg",
  "arrow-right": "../assets/vector/icons/arrow-right.svg",
  "arrow-narrow-left": "../assets/vector/icons/arrow-narrow-left.svg",
  menu: "../assets/vector/icons/menu.svg",
  "dots-vertical": "../assets/vector/icons/dots-vertical.svg",
  sparkle: "../assets/vector/icons/sparkle.svg",
  "star-06": "../assets/vector/icons/star-06.svg",
  "chevron-down": "../assets/vector/icons/chevron-down.svg",
  "review-rating-color": "../assets/vector/icons/review-rating-color.svg",
  star: "../assets/vector/icons/star.svg",
  "dynamic-star": "../assets/vector/icons/dynamic-star.svg",
  checkmark: "../assets/vector/icons/checkmark.svg",
  close: "../assets/vector/icons/close.svg",
  "arrow-send": "../assets/vector/icons/arrow-send.svg",
  "expand-01": "../assets/vector/icons/expand-01.svg",
  "minimize-01": "../assets/vector/icons/minimize-01.svg",
  "maximize-01": "../assets/vector/icons/maximize-01.svg",
  "monitor-01": "../assets/vector/icons/monitor-01.svg",
  "tablet-01": "../assets/vector/icons/tablet-01.svg",
  "phone-01": "../assets/vector/icons/phone-01.svg",
  "resize-corner": "../assets/vector/icons/resize-corner.svg",
  "settings-01": "../assets/vector/icons/settings-01.svg",
  "sidebar-01": "../assets/vector/icons/sidebar-01.svg",

  filter: "../assets/vector/icons/filter.svg",
  home: "../assets/vector/icons/home.svg",
  "shopexperts-badge": "../assets/vector/icons/shopexperts-badge.svg",
  circle: "../assets/vector/icons/circle.svg",
  "map-pin": "../assets/vector/icons/map-pin.svg",
  "map-01": "../assets/vector/icons/map-01.svg",
  "globe-01": "../assets/vector/icons/globe-01.svg",
  "chat-bubble": "../assets/vector/icons/chat-bubble.svg",
  "link-external": "../assets/vector/icons/link-external.svg",
  "link-external-02": "../assets/vector/icons/link-external-02.svg",
  clock: "../assets/vector/icons/clock.svg",
  calendar: "../assets/vector/icons/calendar.svg",
  copy: "../assets/vector/icons/copy.svg",
  "copy-url": "../assets/vector/icons/copy-url.svg",
  repeat: "../assets/vector/icons/repeat.svg",
  "refresh-cw": "../assets/vector/icons/refresh-cw.svg",
  "shopexperts-star": "../assets/vector/icons/shopexperts-star.svg",
  check: "../assets/vector/icons/check.svg",
  /** @deprecated use check-circle-color instead, this may be replaced with a non-color icon in the future */
  "check-circle": "../assets/vector/icons/check-circle.svg",
  /** Untitled UI check-circle (currentColor outline). */
  "check-circle-outline": "../assets/vector/icons/check-circle-outline.svg",
  /** Untitled UI check-circle-broken (currentColor outline, check overflows the circle). */
  "check-circle-broken": "../assets/vector/icons/check-circle-broken.svg",
  lock: "../assets/vector/icons/lock.svg",
  "lock-open": "../assets/vector/icons/lock-open.svg",
  "image-placeholder": "../assets/vector/icons/image-placeholder.svg",
  image: "../assets/vector/icons/image.svg",
  "file-placeholder": "../assets/vector/icons/file-placeholder.svg",
  "package-placeholder": "../assets/vector/icons/package-placeholder.svg",
  "marker-pin": "../assets/vector/icons/marker-pin.svg",
  delete: "../assets/vector/icons/delete.svg",
  pencil: "../assets/vector/icons/pencil.svg",
  "share-07": "../assets/vector/icons/share-07.svg",
  upload: "../assets/vector/icons/upload.svg",
  "download-01": "../assets/vector/icons/download-01.svg",
  plus: "../assets/vector/icons/plus.svg",
  minus: "../assets/vector/icons/minus.svg",
  "plus-square": "../assets/vector/icons/plus-square.svg",
  preview: "../assets/vector/icons/preview.svg",
  play: "../assets/vector/icons/play.svg",
  "user-circle": "../assets/vector/icons/user-circle.svg",
  "user-square": "../assets/vector/icons/user-square.svg",
  "shop-square": "../assets/vector/icons/shop-square.svg",
  "drag-handle": "../assets/vector/icons/drag-handle.svg",
  "info-circle": "../assets/vector/icons/info-circle.svg",
  "help-circle": "../assets/vector/icons/help-circle.svg",
  "x-circle": "../assets/vector/icons/x-circle.svg",
  "dollar-circle": "../assets/vector/icons/dollar-circle.svg",
  wallet: "../assets/vector/icons/wallet.svg",
  category: "../assets/vector/icons/category.svg",
  "app-simple": "../assets/vector/icons/app-simple.svg",
  // Dashboard sidebar nav icons
  overview: "../assets/vector/icons/overview.svg",
  leads: "../assets/vector/icons/leads.svg",
  listing: "../assets/vector/icons/listing.svg",
  reviews: "../assets/vector/icons/reviews.svg",
  settings: "../assets/vector/icons/settings.svg",
  logout: "../assets/vector/icons/logout.svg",
  // Color icons
  "calendar-color": "../assets/vector/icons/calendar-color.svg",
  "check-verified-color": "../assets/vector/icons/check-verified-color.svg",
  /** Untitled UI check-verified-02, filled blue colorway from the profile design. */
  "check-verified-02": "../assets/vector/icons/check-verified-02.svg",
  "shield-tick-color": "../assets/vector/icons/shield-tick-color.svg",
  "shield-tick": "../assets/vector/icons/shield-tick.svg",
  "thumbs-up-color": "../assets/vector/icons/thumbs-up-color.svg",
  "check-circle-color": "../assets/vector/icons/check-circle-color.svg",
  /** Filled blue circle with a white "i" from the claim-listing design. */
  "info-circle-color": "../assets/vector/icons/info-circle-color.svg",
  // Social media icons
  linkedin: "../assets/vector/icons/brands/linkedin.svg",
  twitter: "../assets/vector/icons/brands/twitter.svg",
  facebook: "../assets/vector/icons/brands/facebook.svg",
  "linkedin-mono": "../assets/vector/icons/brands/linkedin-mono.svg",
  "x-mono": "../assets/vector/icons/brands/x-mono.svg",
  "facebook-mono": "../assets/vector/icons/brands/facebook-mono.svg",
  "link-mono": "../assets/vector/icons/brands/url-mono.svg",
  "url-mono": "../assets/vector/icons/brands/url-mono.svg",
  github: "../assets/vector/icons/brands/github.svg",
  fiverr: "../assets/vector/icons/brands/fiverr.svg",
  upwork: "../assets/vector/icons/brands/upwork.svg",
  toptal: "../assets/vector/icons/brands/toptal.svg",
  storetasker: "../assets/vector/icons/brands/storetasker.svg",
  "shopify-directory": "../assets/vector/icons/brands/shopify-directory.svg",
} as const;

// Create lazy loaders for all SVGs under the icons directory
const iconModules = import.meta.glob("../assets/vector/icons/**/*.svg");

const icon = computed(() => {
  const path = ICON_PATHS[props.name];
  const loader = iconModules[path];

  // Fallback to a simple empty component if icon not found
  const Empty = { render: () => null } as any;

  if (!loader) return Empty;

  return defineAsyncComponent(async () => {
    const mod: any = await (loader as () => Promise<any>)();
    return mod?.default ?? mod ?? Empty;
  });
});
</script>

<template>
  <component :is="icon" :class="props.class" />
</template>
